import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import { projects } from "@/data/projects";

const considerations = [
  "The property itself and its surrounding development",
  "Infrastructure and servicing context",
  "Market demand within the surrounding community",
  "Project economics and execution requirements",
  "Potential risks affecting the opportunity",
];

const Home = () => {
  useEffect(() => {
    document.title = "Stelcore Development | Real Estate Development — Eastern Ontario";
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="home-hero">
        <div className="container-x py-16 md:py-28 lg:py-36">
          <div className="max-w-4xl">
            <h1 className="sr-only">Stelcore Development — Real Estate Development in Eastern Ontario</h1>
            <motion.p
              className="text-base text-graphite leading-relaxed max-w-md mb-10"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
            >
              Stelcore Development is a privately owned Eastern Ontario real estate development
              company. We identify strategic real estate opportunities and advance projects through
              acquisition, planning, development, and execution. We are always looking for new
              opportunities.
            </motion.p>
            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Link to="/projects" className="btn-primary" data-testid="hero-cta-projects">
                View Our Projects <ArrowRight size={14} />
              </Link>
              <Link to="/invest-with-us" className="btn-secondary" data-testid="hero-cta-invest">
                Invest With Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AERIAL BAND */}
      <section className="border-b border-ink/10" data-testid="aerial-band">
        <Reveal>
          <div className="overflow-hidden">
            <motion.img
              src="/assets/aerial-development.jpg"
              alt="Aerial view of a Stelcore development site under construction in Eastern Ontario"
              className="w-full h-[300px] md:h-[480px] lg:h-[560px] object-cover"
              initial={{ scale: 1.08 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.8, ease: "easeOut" }}
            />
          </div>
        </Reveal>
      </section>

      {/* SELECTED PROJECTS */}
      <section className="border-b border-ink/10" data-testid="selected-projects">
        <div className="container-x py-20 md:py-28">
          <Reveal>
            <div className="mt-2 mb-14">
              <p className="text-sm md:text-base tracking-[0.25em] uppercase font-bold text-ink">Projects</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((p, i) => (
              <ProjectCard key={p.slug} project={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* INVEST WITH US — DARK */}
      <section className="bg-ink text-bone" data-testid="invest-cta-section">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel dark>Invest With Us</SectionLabel>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold uppercase mt-6 mb-4">
                Have a property opportunity?
              </h2>
              <p className="text-lg text-bone/70 mb-8">
                Your property may hold more potential than a traditional sale.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-sm text-bone/60 leading-relaxed mb-6">
                Many property owners hold real estate with meaningful development potential but do not
                have the time, development experience, capital or desire to take the property through
                the development process themselves.
              </p>
              <p className="text-sm text-bone/60 leading-relaxed mb-10">
                For the right property, Stelcore Development may consider a structured arrangement where
                the owner contributes the property to the development opportunity and participates
                alongside Stelcore as the project advances.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/invest-with-us#submit" className="btn-light" data-testid="invest-cta-discuss">
                  Discuss Your Property <ArrowRight size={14} />
                </Link>
                <Link to="/invest-with-us#how-it-works" className="btn-outline-light" data-testid="invest-cta-how">
                  How It Works
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DEVELOPMENT DISCIPLINE */}
      <section data-testid="development-discipline">
        <div className="container-x py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold uppercase mt-2 mb-8">
                Development begins with understanding the property.
              </h2>
              <p className="text-base text-graphite leading-relaxed mb-6">
                Real estate development requires careful evaluation before a project moves
                forward. Stelcore considers the property, surrounding development, infrastructure, market
                demand, project economics, execution requirements and potential risks when evaluating
                opportunities.
              </p>
              <p className="text-base text-graphite leading-relaxed">
                Our past projects demonstrate a focus on tangible real estate opportunities
                supported by professional work and project documentation.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-graphite mb-6">What We Consider</p>
              <ul className="border-t border-ink/10">
                {considerations.map((c) => (
                  <li key={c} className="border-b border-ink/10 py-4 text-sm text-ink">
                    {c}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
